import React from 'react'
import { FileText, Calendar, DollarSign, Eye, Download } from 'lucide-react'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, ShadcnButton } from '../ui'
import { FiscalDocument } from '../../modules/financial/FiscalDocumentsPage'
import { formatCurrency, formatDateToBR } from '../../utils/formatters'

interface FiscalDocumentsListViewProps {
 documents: FiscalDocument[]
 isLoading: boolean
 onViewDetails: (documentId: string) => void
 onDownloadXML: (documentId: string) => void
 getStatusIcon: (status: FiscalDocument['status']) => React.ReactNode
 getStatusLabel: (status: FiscalDocument['status']) => string
 page: number
 totalPages: number
 onPageChange: (page: number) => void
}

export const FiscalDocumentsListView: React.FC<FiscalDocumentsListViewProps> = ({
 documents,
 isLoading,
 onViewDetails,
 onDownloadXML,
 getStatusIcon,
 getStatusLabel,
 page,
 totalPages,
 onPageChange,
}) => {
 if (isLoading && documents.length === 0) {
 return (
 <div className="flex items-center justify-center py-12">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
 </div>
 )
 }

 if (documents.length === 0) {
 return (
 <div className="text-center py-12">
 <FileText className="h-16 w-16 mx-auto text-muted-foreground dark:text-muted-foreground mb-4"/>
 <p className="text-muted-foreground">Nenhum documento fiscal encontrado</p>
 </div>
 )
 }

 return (
 <div className="space-y-4">
 <div className="overflow-x-auto">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>Número</TableHead>
 <TableHead>Série</TableHead>
 <TableHead>Chave de Acesso</TableHead>
 <TableHead>Data de Emissão</TableHead>
 <TableHead>Emitente</TableHead>
 <TableHead>Cliente</TableHead>
 <TableHead>Valor Total</TableHead>
 <TableHead>Status</TableHead>
 <TableHead>Ações</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {documents.map((doc) => {
 const totalValue = parseFloat(doc.totalValue.toString())

 return (
 <TableRow key={doc.id}>
 <TableCell>
 <span className="font-medium">{doc.number || 'N/A'}</span>
 </TableCell>
 <TableCell>
 <span>{doc.series || 'N/A'}</span>
 </TableCell>
 <TableCell>
 <span className="text-xs font-mono truncate max-w-xs block">
 {doc.accessKey || 'N/A'}
 </span>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-muted-foreground"/>
 <span>
 {doc.issueDate ? formatDateToBR(doc.issueDate) : 'N/A'}
 </span>
 </div>
 </TableCell>
 <TableCell>
 <span className="truncate max-w-xs block">{doc.emitterName}</span>
 </TableCell>
 <TableCell>
 <span className="truncate max-w-xs block">{doc.customerName}</span>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <DollarSign className="h-4 w-4 text-green-600"/>
 <span className="font-medium">{formatCurrency(totalValue)}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 {getStatusIcon(doc.status)}
 <span>{getStatusLabel(doc.status)}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex gap-1">
 <ShadcnButton
 variant="ghost"
 size="sm"
 onClick={() => onViewDetails(doc.id)}
 >
 <Eye className="h-4 w-4"/>
 </ShadcnButton>
 {doc.xml && (
 <ShadcnButton
 variant="ghost"
 size="sm"
 onClick={() => onDownloadXML(doc.id)}
 title="Baixar XML"
 >
 <Download className="h-4 w-4"/>
 </ShadcnButton>
 )}
 </div>
 </TableCell>
 </TableRow>
 )
 })}
 </TableBody>
 </Table>
 </div>

 {/* Pagination */}
 {totalPages > 1 && (
 <div className="flex items-center justify-between">
 <p className="text-sm text-muted-foreground">
 Página {page} de {totalPages}
 </p>
 <div className="flex gap-2">
 <ShadcnButton
 variant="outline"
 size="sm"
 onClick={() => onPageChange(page - 1)}
 disabled={page === 1}
 >
 Anterior
 </ShadcnButton>
 <ShadcnButton
 variant="outline"
 size="sm"
 onClick={() => onPageChange(page + 1)}
 disabled={page === totalPages}
 >
 Próxima
 </ShadcnButton>
 </div>
 </div>
 )}
 </div>
 )
}

